import { select } from "d3";

import {
  AppletElement,
  EdgeEndpoints,
  GroupSelection,
  NODE_STROKE,
  SvgSelection,
  renderApplet,
  trimmedEdgeEndpoints,
} from "./common";

type BananaNodeKind = "root" | "connector" | "center" | "leaf";

type BananaNode = Readonly<{
  id: string;
  x: number;
  y: number;
  kind: BananaNodeKind;
}>;

type BananaEdgeKind = "root" | "branch" | "leaf";

type BananaEdge = Readonly<{
  source: string;
  target: string;
  kind: BananaEdgeKind;
}>;

const BANANA_WIDTH = 620;
const BANANA_HEIGHT = 250;
const ROOT_RADIUS = 19;
const CONNECTOR_RADIUS = 14;
const CENTER_RADIUS = 17;
const LEAF_RADIUS = 9;
const ROOT_FILL = "#f6f7f2";
const CONNECTOR_FILL = "#fff";
const CENTER_FILL = "#edf7f5";
const LEAF_FILL = "#fff7e7";
const ROOT_EDGE_STROKE = "#8d9a8d";
const BRANCH_EDGE_STROKE = "#aeb7aa";
const LEAF_EDGE_STROKE = "#c99a45";

const BANANA_NODES: readonly BananaNode[] = [
  { id: "v0", x: 310, y: 42, kind: "root" },
  { id: "p1", x: 88, y: 106, kind: "connector" },
  { id: "p2", x: 236, y: 106, kind: "connector" },
  { id: "p3", x: 384, y: 106, kind: "connector" },
  { id: "p4", x: 532, y: 106, kind: "connector" },
  { id: "w1", x: 88, y: 170, kind: "center" },
  { id: "w2", x: 236, y: 170, kind: "center" },
  { id: "w3", x: 384, y: 170, kind: "center" },
  { id: "w4", x: 532, y: 170, kind: "center" },
  { id: "u11", x: 54, y: 220, kind: "leaf" },
  { id: "u12", x: 88, y: 226, kind: "leaf" },
  { id: "u13", x: 122, y: 220, kind: "leaf" },
  { id: "u21", x: 236, y: 226, kind: "leaf" },
  { id: "u31", x: 334, y: 214, kind: "leaf" },
  { id: "u32", x: 368, y: 226, kind: "leaf" },
  { id: "u33", x: 400, y: 226, kind: "leaf" },
  { id: "u34", x: 434, y: 214, kind: "leaf" },
  { id: "u41", x: 512, y: 224, kind: "leaf" },
  { id: "u42", x: 552, y: 224, kind: "leaf" },
];

const BANANA_EDGES: readonly BananaEdge[] = [
  { source: "v0", target: "p1", kind: "root" },
  { source: "v0", target: "p2", kind: "root" },
  { source: "v0", target: "p3", kind: "root" },
  { source: "v0", target: "p4", kind: "root" },
  { source: "p1", target: "w1", kind: "branch" },
  { source: "p2", target: "w2", kind: "branch" },
  { source: "p3", target: "w3", kind: "branch" },
  { source: "p4", target: "w4", kind: "branch" },
  { source: "w1", target: "u11", kind: "leaf" },
  { source: "w1", target: "u12", kind: "leaf" },
  { source: "w1", target: "u13", kind: "leaf" },
  { source: "w2", target: "u21", kind: "leaf" },
  { source: "w3", target: "u31", kind: "leaf" },
  { source: "w3", target: "u32", kind: "leaf" },
  { source: "w3", target: "u33", kind: "leaf" },
  { source: "w3", target: "u34", kind: "leaf" },
  { source: "w4", target: "u41", kind: "leaf" },
  { source: "w4", target: "u42", kind: "leaf" },
];

function nodeLookup(): ReadonlyMap<string, BananaNode> {
  return new Map(BANANA_NODES.map((node: BananaNode): [string, BananaNode] => [node.id, node]));
}

function nodeRadius(node: BananaNode): number {
  switch (node.kind) {
    case "root":
      return ROOT_RADIUS;
    case "connector":
      return CONNECTOR_RADIUS;
    case "center":
      return CENTER_RADIUS;
    case "leaf":
      return LEAF_RADIUS;
  }
}

function nodeFill(node: BananaNode): string {
  switch (node.kind) {
    case "root":
      return ROOT_FILL;
    case "connector":
      return CONNECTOR_FILL;
    case "center":
      return CENTER_FILL;
    case "leaf":
      return LEAF_FILL;
  }
}

function nodeStrokeWidth(node: BananaNode): number {
  switch (node.kind) {
    case "root":
      return 1.8;
    case "connector":
      return 1.4;
    case "center":
      return 1.7;
    case "leaf":
      return 1.3;
  }
}

function edgeStroke(edge: BananaEdge): string {
  switch (edge.kind) {
    case "root":
      return ROOT_EDGE_STROKE;
    case "branch":
      return BRANCH_EDGE_STROKE;
    case "leaf":
      return LEAF_EDGE_STROKE;
  }
}

function edgeStrokeWidth(edge: BananaEdge): number {
  switch (edge.kind) {
    case "root":
      return 2.5;
    case "branch":
      return 2.2;
    case "leaf":
      return 1.8;
  }
}

function edgeEndpoints(edge: BananaEdge, nodes: ReadonlyMap<string, BananaNode>): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(source, target, nodeRadius(source), nodeRadius(target));
}

function appendSvg(element: AppletElement): SvgSelection {
  return select<AppletElement, unknown>(element)
    .append<SVGSVGElement>("svg")
    .attr("viewBox", `0 0 ${BANANA_WIDTH} ${BANANA_HEIGHT}`)
    .attr("width", BANANA_WIDTH)
    .attr("height", BANANA_HEIGHT)
    .attr("role", "img")
    .attr("aria-label", "An unlabeled banana tree with four branches and leaf groups of sizes three, one, four, and two.");
}

function renderEdges(graph: GroupSelection): void {
  const nodes = nodeLookup();

  graph.append<SVGGElement>("g")
    .attr("class", "banana-example-edges")
    .selectAll<SVGLineElement, BananaEdge>("line")
    .data(BANANA_EDGES)
    .join("line")
    .attr("x1", (edge: BananaEdge): number => edgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: BananaEdge): number => edgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: BananaEdge): number => edgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: BananaEdge): number => edgeEndpoints(edge, nodes).y2)
    .attr("stroke", (edge: BananaEdge): string => edgeStroke(edge))
    .attr("stroke-width", (edge: BananaEdge): number => edgeStrokeWidth(edge))
    .attr("stroke-linecap", "round");
}

function renderNodes(graph: GroupSelection): void {
  graph.append<SVGGElement>("g")
    .attr("class", "banana-example-nodes")
    .selectAll<SVGCircleElement, BananaNode>("circle")
    .data(BANANA_NODES)
    .join("circle")
    .attr("cx", (node: BananaNode): number => node.x)
    .attr("cy", (node: BananaNode): number => node.y)
    .attr("r", (node: BananaNode): number => nodeRadius(node))
    .attr("fill", (node: BananaNode): string => nodeFill(node))
    .attr("stroke", NODE_STROKE)
    .attr("stroke-width", (node: BananaNode): number => nodeStrokeWidth(node))
    .attr("data-node-kind", (node: BananaNode): BananaNodeKind => node.kind);
}

function renderBananaExample(element: AppletElement): void {
  element.dataset.rendered = "true";
  element.replaceChildren();

  const graph = appendSvg(element)
    .append<SVGGElement>("g")
    .attr("class", "banana-example-graph");

  renderEdges(graph);
  renderNodes(graph);
}

renderApplet(".banana-example-applet", renderBananaExample);
